import {
  resolveGatewayBaseUrl,
  resolveGatewayLoginApiBase,
} from "../runtime/gatewayEnv";

/**
 * Dynamic application access helpers — driven by API `applications[]` only.
 */

export type AssignedApplication = {
  id: string;
  name: string;
  url: string;
};

export type LoginSelectedApplication = {
  id?: string;
  url?: string;
  name?: string;
};

const LOGIN_SELECTED_APP_STORAGE_KEY = "variphi_login_selected_app";

function isUrlLike(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  return /^(localhost|127\.0\.0\.1)(:\d+)?/i.test(trimmed);
}

/** Human-readable application label — never returns a URL. */
export function getApplicationDisplayName(app: AssignedApplication): string {
  const name = String(app.name ?? "").trim();
  if (name && !isUrlLike(name)) return name;
  return "Application";
}

/** Resolve the login selection label without exposing URLs. */
export function resolveSelectedApplicationDisplayName(
  selection: LoginSelectedApplication | null | undefined,
  candidates: AssignedApplication[] = [],
): string {
  if (!selection) return "the application you selected";

  const enriched = enrichLoginSelection(selection, candidates);
  const directName = String(enriched?.name ?? selection.name ?? "").trim();
  if (directName && !isUrlLike(directName)) return directName;

  const selectedId = String(enriched?.id ?? selection.id ?? "").trim();
  if (selectedId) {
    const byId = candidates.find((app) => app.id && app.id === selectedId);
    if (byId) return getApplicationDisplayName(byId);
  }

  const selectedOrigin = normalizeApplicationUrlOrigin(
    enriched?.url ?? selection.url,
  );
  if (selectedOrigin) {
    for (const app of candidates) {
      const appOrigin = normalizeApplicationUrlOrigin(app.url);
      if (appOrigin && appOrigin === selectedOrigin) {
        return getApplicationDisplayName(app);
      }
    }
  }

  return "the application you selected";
}

let cachedAllApplications: AssignedApplication[] | null = null;

/** Public application registry — used to resolve names for unassigned selections. */
export async function fetchAllApplications(): Promise<AssignedApplication[]> {
  if (cachedAllApplications) return cachedAllApplications;
  if (typeof window === "undefined") return [];

  const base = gatewayBaseUrl();
  try {
    const response = await fetch(`${base}/client/application`, {
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!response.ok) return [];

    const payload = (await response.json()) as {
      data?: unknown;
      applications?: unknown;
    };
    const rows = payload?.data ?? payload?.applications ?? payload;
    const parsed = parseAssignedApplications(rows);
    if (parsed.length > 0) {
      cachedAllApplications = parsed;
    }
    return parsed;
  } catch {
    return [];
  }
}

export function normalizeExternalApplicationUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const bare = trimmed.replace(/^\/\//, "");
  const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(bare);
  return `${isLocal ? "http" : "https"}://${bare}`;
}

export function resolveThisAppPublicUrl(): string {
  const fromEnv = (process.env.REACT_APP_APPLICATION_URL ?? "").trim();
  if (fromEnv) {
    return normalizeExternalApplicationUrl(fromEnv);
  }
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "http://localhost:3700";
}

/** Bounce auth callback off a stale gateway host (e.g. senrysa.in → staging.vgiskill.ai). */
export function resolveConfiguredCallbackBounceUrl(
  currentOrigin?: string,
): string | null {
  const configuredUrl = (process.env.REACT_APP_APPLICATION_URL ?? "").trim();
  if (!configuredUrl) return null;

  const targetOrigin = normalizeApplicationUrlOrigin(configuredUrl);
  const origin = (
    currentOrigin ??
    (typeof window !== "undefined" ? window.location.origin : "")
  ).toLowerCase();
  if (!targetOrigin || targetOrigin === origin) return null;

  return normalizeExternalApplicationUrl(configuredUrl);
}

export function normalizeApplicationUrlOrigin(
  rawUrl: string | undefined | null,
): string | null {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;
  try {
    const href = normalizeExternalApplicationUrl(trimmed);
    if (!href) return null;
    return new URL(href).origin.toLowerCase();
  } catch {
    return null;
  }
}

export function parseAssignedApplications(raw: unknown): AssignedApplication[] {
  if (!Array.isArray(raw)) return [];

  const parsed: AssignedApplication[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as {
      id?: unknown;
      applicationId?: unknown;
      application_id?: unknown;
      name?: unknown;
      url?: unknown;
    };
    const id = String(
      record.id ?? record.applicationId ?? record.application_id ?? "",
    ).trim();
    const name = String(record.name ?? "").trim();
    let url = normalizeExternalApplicationUrl(String(record.url ?? ""));
    const configuredId = (process.env.REACT_APP_APPLICATION_ID ?? "").trim();
    const configuredUrl = (process.env.REACT_APP_APPLICATION_URL ?? "").trim();
    if (configuredId && configuredUrl && id === configuredId) {
      url = normalizeExternalApplicationUrl(configuredUrl);
    }
    if (!id && !url) continue;
    parsed.push({ id, name: name || url, url });
  }
  return parsed;
}

/** Ensure auth/me is called with applications include flags. */
export function normalizeSessionEndpoint(endpoint: string): string {
  let trimmed = endpoint.trim();
  if (!trimmed) return "/client/auth/me?applications=true&cloudflareApplications=true";

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      trimmed = `${u.pathname}${u.search}`;
    } catch {
      // fall through with original value
    }
  }

  if (/applications=/i.test(trimmed)) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }
  const [path] = trimmed.split("?");
  const basePath = path.startsWith("/") ? path : `/${path}`;
  return `${basePath}?applications=true&cloudflareApplications=true`;
}

/** Merge applications from raw auth/me onto the unwrapped user profile. */
export function mergeApplicationsOntoSessionUser(
  user: { applications?: unknown },
  raw: unknown,
): void {
  const fromUser = parseAssignedApplications(user.applications);
  if (fromUser.length > 0) return;

  const extracted = extractApplicationsFromAuthMePayload(raw);
  const parsed = parseAssignedApplications(extracted);
  if (parsed.length > 0) {
    user.applications = parsed;
  }
}

/** Resolve applications from auth_user or nested auth_me_response envelope. */
export function resolveAssignedApplicationsForSession(
  user: { applications?: unknown } | null | undefined,
): AssignedApplication[] {
  const fromUser = parseAssignedApplications(user?.applications);
  if (fromUser.length > 0) return fromUser;

  if (typeof window === "undefined") return [];

  try {
    const raw = localStorage.getItem("auth_me_response");
    if (!raw) return [];
    const payload = JSON.parse(raw) as {
      data?: { applications?: unknown; data?: { applications?: unknown } };
      applications?: unknown;
    };
    const layer = payload?.data;
    const nested =
      layer && typeof layer === "object" && !Array.isArray(layer)
        ? (layer as { data?: { applications?: unknown }; applications?: unknown })
        : null;
    const candidates = [
      nested?.data?.applications,
      nested?.applications,
      layer && !Array.isArray(layer) ? (layer as { applications?: unknown }).applications : undefined,
      payload?.applications,
    ];
    for (const candidate of candidates) {
      const parsed = parseAssignedApplications(candidate);
      if (parsed.length > 0) return parsed;
    }
  } catch {
    /* ignore */
  }

  return [];
}

/** Fill missing selection.url from assigned applications (e.g. callback only has app_id). */
export function enrichLoginSelection(
  selection: LoginSelectedApplication | null | undefined,
  assigned: AssignedApplication[],
): LoginSelectedApplication | null {
  if (!selection) return null;

  const id = String(selection.id ?? "").trim() || undefined;
  const url = String(selection.url ?? "").trim() || undefined;
  const name = String(selection.name ?? "").trim() || undefined;

  if (url) {
    return { id, url, name };
  }

  if (id) {
    const match = assigned.find((app) => app.id === id);
    if (match) {
      return { id: match.id, url: match.url, name: match.name || name };
    }
  }

  return id || name ? { id, url, name } : null;
}

/** Extract applications[] from any auth/me envelope shape. */
export function extractApplicationsFromAuthMePayload(
  raw: unknown,
): unknown {
  if (!raw || typeof raw !== "object") return undefined;

  const visit = (node: unknown): unknown => {
    if (!node || typeof node !== "object" || Array.isArray(node)) return undefined;
    const record = node as { applications?: unknown; data?: unknown };
    if (Array.isArray(record.applications) && record.applications.length > 0) {
      return record.applications;
    }
    if (record.data && typeof record.data === "object") {
      return visit(record.data);
    }
    return undefined;
  };

  return visit(raw);
}

/** Route guard — access is granted only when the current browser origin is assigned. */
export function userHasApplicationAccess(
  user: { applications?: unknown } | null | undefined,
  options?: {
    currentOrigin?: string;
  },
): boolean {
  const assigned = resolveAssignedApplicationsForSession(user);
  const currentOrigin = (
    options?.currentOrigin ??
    (typeof window !== "undefined" ? window.location.origin : "")
  ).toLowerCase();
  return userHasAccessToCurrentOrigin(assigned, currentOrigin);
}

/** Match the assigned application row for a login selection (id preferred, then origin). */
export function resolveMatchedAssignedApplication(
  assignedRaw: unknown,
  selection: LoginSelectedApplication | null | undefined,
): AssignedApplication | null {
  const assigned = parseAssignedApplications(assignedRaw);
  const enriched = enrichLoginSelection(selection, assigned);
  if (!enriched) return null;

  const selectedId = String(enriched.id ?? "").trim();
  const selectedOrigin = normalizeApplicationUrlOrigin(enriched.url);

  if (selectedId) {
    const byId = assigned.find((app) => app.id && app.id === selectedId);
    if (byId) return byId;
  }

  if (selectedOrigin) {
    for (const app of assigned) {
      const appOrigin = normalizeApplicationUrlOrigin(app.url);
      if (appOrigin && appOrigin === selectedOrigin) {
        return app;
      }
    }
  }

  return null;
}

/** Post-login callback — validates the app the user picked on the login page. */
export function evaluateLoginApplicationAccess(
  user: { applications?: unknown } | null | undefined,
  selection: LoginSelectedApplication | null | undefined,
  currentOrigin?: string,
): boolean {
  const assigned = resolveAssignedApplicationsForSession(user);
  const enriched = enrichLoginSelection(selection, assigned);
  const origin = (
    currentOrigin ??
    (typeof window !== "undefined" ? window.location.origin : "")
  ).toLowerCase();

  if (!enriched?.id && !enriched?.url) {
    return userHasAccessToCurrentOrigin(assigned, origin);
  }

  const matched = resolveMatchedAssignedApplication(assigned, enriched);
  if (!matched) {
    return false;
  }

  const assignedOrigin = normalizeApplicationUrlOrigin(matched.url);
  return Boolean(assignedOrigin && assignedOrigin === origin);
}

/** Cross-origin bounce target when login selection is assigned but callback landed elsewhere. */
export function resolveCrossOriginBounceUrl(
  user: { applications?: unknown } | null | undefined,
  selection: LoginSelectedApplication | null | undefined,
  currentOrigin?: string,
): string | null {
  const configuredBounce = resolveConfiguredCallbackBounceUrl(currentOrigin);
  if (configuredBounce) return configuredBounce;

  const assigned = resolveAssignedApplicationsForSession(user);
  const matched = resolveMatchedAssignedApplication(assigned, selection);
  if (!matched?.url) return null;

  const assignedOrigin = normalizeApplicationUrlOrigin(matched.url);
  const origin = (
    currentOrigin ??
    (typeof window !== "undefined" ? window.location.origin : "")
  ).toLowerCase();
  if (!assignedOrigin || assignedOrigin === origin) return null;

  return matched.url;
}

export function isSelectedApplicationAssigned(
  assignedRaw: unknown,
  selection: LoginSelectedApplication | null | undefined,
): boolean {
  if (!selection) return false;

  const assigned = parseAssignedApplications(assignedRaw);
  if (assigned.length === 0) return false;

  const selectedId = String(selection.id ?? "").trim();
  const selectedOrigin = normalizeApplicationUrlOrigin(selection.url);

  for (const app of assigned) {
    if (selectedId && app.id && selectedId === app.id) {
      return true;
    }
    if (selectedOrigin) {
      const assignedOrigin = normalizeApplicationUrlOrigin(app.url);
      if (assignedOrigin && assignedOrigin === selectedOrigin) {
        return true;
      }
    }
  }

  return false;
}

export function userHasAccessToCurrentOrigin(
  assignedRaw: unknown,
  currentOrigin: string = typeof window !== "undefined"
    ? window.location.origin.toLowerCase()
    : "",
): boolean {
  if (!currentOrigin) return false;

  const assigned = parseAssignedApplications(assignedRaw);
  if (assigned.length === 0) return false;

  for (const app of assigned) {
    const appOrigin = normalizeApplicationUrlOrigin(app.url);
    if (appOrigin && appOrigin === currentOrigin) {
      return true;
    }
  }

  return false;
}

/** @deprecated Use userHasAccessToCurrentOrigin — kept for existing imports during migration. */
export function browserHasAssignedApplication(
  applications: unknown,
  currentOrigin?: string,
): boolean {
  return userHasAccessToCurrentOrigin(applications, currentOrigin);
}

export function captureLoginSelectionFromCallback(): LoginSelectedApplication | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const hasToken = params.has("token") || params.has("auth");
  if (!hasToken) return readLoginSelectedApplication();

  const selection: LoginSelectedApplication = {
    id: params.get("app_id")?.trim() || undefined,
    url: params.get("app_url")?.trim() || undefined,
    name: params.get("app_name")?.trim() || undefined,
  };

  if (!selection.id && !selection.url) {
    clearLoginSelection();
    return null;
  }

  try {
    sessionStorage.setItem(
      LOGIN_SELECTED_APP_STORAGE_KEY,
      JSON.stringify(selection),
    );
  } catch {
    /* ignore */
  }

  return selection;
}

export function readLoginSelectedApplication(): LoginSelectedApplication | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(LOGIN_SELECTED_APP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LoginSelectedApplication;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      id: String(parsed.id ?? "").trim() || undefined,
      url: String(parsed.url ?? "").trim() || undefined,
      name: String(parsed.name ?? "").trim() || undefined,
    };
  } catch {
    return null;
  }
}

export function clearLoginSelection(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(LOGIN_SELECTED_APP_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function persistLoginSelection(
  selection: LoginSelectedApplication,
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      LOGIN_SELECTED_APP_STORAGE_KEY,
      JSON.stringify(selection),
    );
  } catch {
    /* ignore */
  }
}

function gatewayBaseUrl(): string {
  return resolveGatewayBaseUrl() || "http://localhost:4702";
}

/** Register target app on gateway before navigating from the denied page. */
export async function registerGatewayPostLoginTarget(
  app: AssignedApplication,
): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    await fetch(`${resolveGatewayLoginApiBase()}/login/select-app`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: AbortSignal.timeout(1500),
      body: JSON.stringify({
        url: app.url,
        name: app.name,
        applicationId: app.id,
      }),
    });
  } catch {
    /* best-effort */
  }
}

/** Open an assigned application from the denied page without a wrong-port SSO bounce. */
export async function openAssignedApplication(
  app: AssignedApplication,
): Promise<void> {
  if (typeof window === "undefined") return;

  const targetUrl = normalizeExternalApplicationUrl(app.url);
  if (!targetUrl) return;

  const normalizedApp: AssignedApplication = {
    ...app,
    url: targetUrl,
  };

  await registerGatewayPostLoginTarget(normalizedApp);

  const targetOrigin = normalizeApplicationUrlOrigin(targetUrl);
  const currentOrigin = window.location.origin.toLowerCase();

  if (targetOrigin && targetOrigin === currentOrigin) {
    clearLoginSelection();
    try {
      const dest = new URL(targetUrl);
      window.location.assign(dest.toString());
    } catch {
      window.location.assign("/");
    }
    return;
  }

  persistLoginSelection({
    id: normalizedApp.id,
    url: targetUrl,
    name: normalizedApp.name,
  });

  try {
    window.location.assign(targetUrl);
  } catch {
    window.location.href = targetUrl;
  }
}
